import { useContext, useEffect, useState } from 'react';
import { FC } from 'react';
import tw, { styled } from 'twin.macro';
import { getCitiesByCountryID, getCountriesList } from '../../../../lib/api/country';
import { courseOptions, PhoneOptions } from '../common/interface';
import { courseListData } from '../../../../lib/api/course';
import { ChevronDownIcon } from '@heroicons/react/solid';

import { stripTags } from '../../../../lib/common/common';
import { CommonContext } from '../../../../pages/_app';
import { UserLocation } from '../../../../interfaces/city';
import { getUserLocationData } from '../../../../lib/common/userLocation';

const Container = tw.div`
    relative w-full h-full
`;
const SelectedValue = tw.div`
    cursor-pointer leading-[1.42857143] pl-[11px] py-2 text-[#231f20] w-full h-full flex justify-between items-center
`;

const Arrow = tw.div`
    inline-flex items-center pr-3
`;

const Input = tw.input`cursor-pointer w-full h-full outline-none border-0 text-[13px] leading-5 text-[#231f20]`;

const Div = tw.span`cursor-pointer w-full h-full outline-none border-0 text-[13px] leading-5 text-[#231f20] whitespace-nowrap overflow-hidden`;

const OptionsContainer = styled.div(({ fullWidth, optionTop, open, halfHeight }) => [
  tw`hidden`,
  !optionTop &&
    tw`z-[220] w-60 absolute top-10 left-0 max-h-[220px] overflow-y-scroll border text-gray-900 border-gray-300 rounded-[3px] bg-white`,
  optionTop &&
    tw`z-[220] absolute left-0 max-h-[220px] overflow-y-scroll border text-gray-900 border-gray-300 rounded-[3px] bg-white bottom-full`,
  fullWidth && tw`w-full max-w-full min-w-[220px]`,
  open && tw`block`,
  halfHeight && tw`!max-h-[150px]`,
]);

const OptionsGroup = tw.div`
    border-b-2 relative
`;

const ISDOption = styled.div(({ selected }) => [
  tw`
  flex items-center cursor-pointer py-2 px-2 text-[13px] leading-[1.42857143] text-[#555]
  hover:(bg-gray-100)
`,
  // selected && tw`bg-gray-100 top-0`,
]);

const Option = styled.div(({ selected }) => [
  tw`
  cursor-pointer py-2 px-2 text-[13px] leading-[1.42857143] text-[#555]
  hover:(bg-gray-100)
`,
  // selected && tw`bg-gray-100 top-0`,
]);

const PhoneCode = tw.div`
    w-12 text-[13px] leading-[1.42857143] text-[#555]
`;

const Country = tw.div`
    w-full text-[13px] leading-[1.42857143] text-[#555]
`;

type Props = {
  type: 'PhoneCode' | 'Country' | 'City' | 'Course' | 'Identification' | 'LearningType' | 'Custom';
  register: any;
  setValue: any;
  isSubmitted: any;
  optionTop?: boolean;
  setSelectedCountry?: any;
  cityArray?: any;
  getValues?: any;
  defaultValue?: any;
  halfHeight?: boolean;
  fullWidth?: boolean;
  custom?: {
    customOptions: {
      id: number | string;
      name: string;
    }[];
    customInputName: string;
    customInputIdName?: string;
    required: boolean;
    pattern?: any;
  };
};

// static
const learningTypes = [
  {
    id: 1,
    name: 'Live Online Classroom',
  },
];

const CustomSelect: FC<Props> = ({
  type,
  register,
  setValue,
  isSubmitted,
  optionTop,
  setSelectedCountry,
  cityArray,
  getValues,
  defaultValue,
  halfHeight,
  custom,
  fullWidth,
}) => {
  const [currentCourse, setCurrentCourse] = useContext(CommonContext);

  const { customInputName, customOptions, required, pattern, customInputIdName } = custom || {};

  // state for dropdown open/close
  const [open, setOpen] = useState<boolean>(false);

  // state for selected value
  const [selectedValue, setSelectedValue] = useState('');

  const [isdList, setIsdList] = useState<PhoneOptions>();
  const [trendingCourses, setTrendingCourses] = useState<courseOptions>([]);
  const [nonTrendingCourses, setNonTrendingCourses] = useState<courseOptions>([]);
  const [trendingCountryCities, setTrendingCountryCities] = useState([]);
  const [nonTrendingCountryCities, setNonTrendingCountryCities] = useState([]);
  const [defaultCityData, setDefaultCityData] = useState([]);

  // Identification options (singapore only)
  const identificationOptions: string[] = ['Singapore Citizen', 'Singapore Permanent Resident', 'Others'];

  // function to open/close dropdown
  const changeOpen = () => {
    setOpen(!open);
  };

  const changeSelectValue = async (value: any) => {
    if (type === 'PhoneCode') {
      setSelectedValue(value.country);
      setValue('isd', value.code ? value.code : 'Select', { shouldValidate: true });
    } else if (type === 'Course') {
      setSelectedValue(value.name);
      setValue('course_name', value.id ? stripTags(value.name) : 'Select Course', { shouldValidate: true });
      setValue('course_id', value.id || value.name);
    } else if (type === 'City') {
      setSelectedValue(value.name);
      setValue('city_name', value.id ? value.name : 'Select City', { shouldValidate: true });
      setValue('city_id', value.id || value.name);
    } else if (type === 'Country') {
      setSelectedValue(value.name);
      setValue('country_name', value.id ? value.name : 'Select Country', { shouldValidate: true });
      setValue('country_id', value.id || value.name);

      const cityData = await getCitiesByCountryID(value.id);
    //console.log(cityData);
      setSelectedCountry(cityData || []);
      setDefaultCityData([]);
      setValue('city_name', '', { shouldValidate: true });
      setValue('city_id', null);
      // console.log('city changed', value.id, value.name,allCountries.filter(ele => ele.id  === value.id)[0])
      // setCitiesList(allCountries.filter(ele => ele.id  === value.id)[0]);
    } else if (type === 'Identification') {
      setSelectedValue(value);
      setValue('identification_type', value.length ? value : 'Select Identification Type', { shouldValidate: true });
    } else if (type === 'LearningType') {
      setValue('learningType', value.id ? value.name : 'Select Learning Type', { shouldValidate: true });
    } else if (type === 'Custom') {
      setSelectedValue(value.name || value);
      setValue(customInputName, value.id ? value.name : 'Select', { shouldValidate: true });
    }
  };

  useEffect(() => {
    // get Countries List
    async function getCountryList() {
      let response = await getCountriesList();
      setIsdList(response.countriesISD);
      setTrendingCountryCities(response.trendingCountryCities);
      setNonTrendingCountryCities(response.nonTrendingCountryCities);
    }

    // getCountriesList
    getCountryList();

    // get Courses List
    async function getCourses() {
      let response = await courseListData();
      setTrendingCourses(response.trending);
      setNonTrendingCourses(response.nonTrending);
      const allTrending = [...response.trending, ...response.nonTrending];
      for (let i in allTrending) {
        if (allTrending[i].id === currentCourse) {
          setValue('course_name', stripTags(allTrending[i].name), { shouldValidate: true });
          setValue('course_id', allTrending[i].id);
          break;
        }
      }
    }
    getCourses();

    // get Selected CountryCities
    async function getSelectedCountryCities() {
      const userLocation = await getUserLocationData();

      if (userLocation?.country?.id) {
        const cityData = await getCitiesByCountryID(userLocation.country.id);
        setDefaultCityData(cityData);
      }

      if (type === 'PhoneCode') {
        setSelectedValue(userLocation?.country?.name);
        setValue('isd', userLocation?.country?.isd);
      } else if (type === 'Course') {
        setSelectedValue(currentCourse);
        //setValue('course_name', '');
        setValue('course_id', currentCourse || null);
      } else if (type === 'City') {
        setSelectedValue(userLocation?.city?.name.replace('+',''));
        setValue('city_name', userLocation?.city?.name || '', { shouldValidate: true });
        setValue('city_id', userLocation?.city?.id || null);
      } else if (type === 'Country') {
        setSelectedValue(userLocation?.country?.name.replace('+',''));
        setValue('country_name', userLocation?.country?.name || '');
        setValue('country_id', userLocation?.country?.id || null);
        setSelectedCountry(userLocation?.country?.id);
      } else if (type === 'Identification') {
        setSelectedValue('Select Identification');
        setValue('identification_type', defaultValue || '');
      } else if (type === 'LearningType') {
        setSelectedValue('Select Learning Type');
        setValue('learningType', '');
      } else if (type === 'Custom') {
        setSelectedValue('Select');
        setValue(customInputName, defaultValue || '');
      }
    }

    getSelectedCountryCities();

    // console.log('calling data');
  }, []);
  // console.log(setSelectedCountry)

  // click check
  useEffect(() => {
    const checkClick = () => {
      // console.log("closing")
      setOpen(false);
    };

    // add event listener
    open && document.body.addEventListener('click', checkClick);

    // cleanup
    return () => {
      // console.log("removing listener");
      document.body.removeEventListener('click', checkClick);
    };
  }, [open]);

  // Validating input value
  const validateValue = (value: any) => {
    // console.log(value, value?.split(' ')[0] !== 'Select');
    if (value.id) {
      return true;
    } else if (!value.length || !value) {
      return false;
    }
    return value?.split(' ')[0] !== 'Select';
  };

  // useEffect(() => {
  //   console.log(getValues("course_name"))
  //   console.log([...trendingCourses, ...nonTrendingCourses]?.filter((ele: any, index: number) => ele.name === getValues('course_name')))
  // },[])

  // console.log(trendingCountryCities, nonTrendingCountryCities)

  // console.log(cityArray, defaultCityData)

  return (
    <Container onClick={changeOpen}>
      <SelectedValue>
        {type === 'PhoneCode' ? (
          <Input readOnly {...register('isd', { required: true, validate: validateValue })} />
        ) : type === 'City' ? (
          <Input
            className="peer"
            placeholder=""
            readOnly
            {...register('city_name', { required: true, validate: validateValue })}
          />
        ) : type === 'Country' ? (
          <Input
            className="peer"
            placeholder=""
            readOnly
            {...register('country_name', { required: true, validate: validateValue })}
          />
        ) : type === 'Course' ? (
          <Input
            className="peer"
            placeholder=""
            readOnly
            {...register('course_name', { required: true, validate: validateValue })}
            // dangerouslySetInnerHTML={{__html: selectedValue}}
          />
        ) : type === 'Identification' ? (
          <Input
            defaultValue={defaultValue}
            className="peer"
            placeholder=""
            readOnly
            {...register('identification_type', { required: true, validate: validateValue })}
          />
        ) : type === 'LearningType' ? (
          <Input
            defaultValue={defaultValue}
            className="peer"
            placeholder=""
            readOnly
            {...register('learningType', { required: required, validate: validateValue })}
          />
        ) : (
          type === 'Custom' && (
            <Input
              defaultValue={defaultValue}
              className="peer"
              placeholder=""
              readOnly
              {...register(customInputName, { required: required, validate: validateValue })}
            />
          )
        )}
        <Arrow>
          <ChevronDownIcon className="w-5" />
        </Arrow>
      </SelectedValue>

      <OptionsContainer
        className="customized-scrollbar"
        fullWidth={fullWidth || type !== 'PhoneCode'}
        optionTop={optionTop}
        open={open}
        halfHeight={halfHeight}
      >
        <OptionsGroup>
          <Option onClick={() => changeSelectValue('')}>Select {type === 'PhoneCode' ? 'ISD' : type}</Option>
        </OptionsGroup>

        {type === 'PhoneCode' ? (
          <>
            <OptionsGroup>
              {isdList?.slice(0, 10).map((ele: any, index: number) => (
                <ISDOption key={index} onClick={() => changeSelectValue(ele)} selected={selectedValue === ele.country}>
                  <PhoneCode>{ele.code}</PhoneCode>
                  <Country>- &nbsp; {ele.country}</Country>
                </ISDOption>
              ))}
            </OptionsGroup>
            <OptionsGroup>
              {isdList
                ?.slice(10)
                .sort((a, b) => (a.country > b.country ? 1 : -1))
                .map((ele: any, index: number) => (
                  <ISDOption
                    key={index}
                    onClick={() => changeSelectValue(ele)}
                    selected={selectedValue === ele.country}
                  >
                    <PhoneCode>{ele.code}</PhoneCode>
                    <Country>- &nbsp; {ele.country}</Country>
                  </ISDOption>
                ))}
            </OptionsGroup>
          </>
        ) : type === 'Course' ? (
          <>
            <OptionsGroup>
              {trendingCourses?.map((ele: any, index: number) => (
                <Option
                  key={index}
                  onClick={() => changeSelectValue(ele)}
                  dangerouslySetInnerHTML={{ __html: ele.name }}
                  selected={selectedValue === ele.name}
                ></Option>
              ))}
            </OptionsGroup>
            <OptionsGroup>
              {nonTrendingCourses
                ?.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                ?.map((ele: any, index: number) => (
                  <Option
                    key={index}
                    onClick={() => changeSelectValue(ele)}
                    dangerouslySetInnerHTML={{ __html: ele.name }}
                    selected={selectedValue === ele.name}
                  ></Option>
                ))}
            </OptionsGroup>
          </>
        ) : type === 'City' ? (
          <>
            {cityArray?.length && (
              <OptionsGroup>
                {cityArray?.map((ele: any, index: number) => (
                  <Option
                    key={index}
                    onClick={() => changeSelectValue(ele)}
                    dangerouslySetInnerHTML={{ __html: ele.name }}
                    selected={selectedValue === ele.name}
                  ></Option>
                ))}
              </OptionsGroup>
            )}

            {/* TODO (check why defaultCityData not being empty) */}
            {!cityArray?.length && (
              <OptionsGroup>
                {defaultCityData?.map((ele: any, index: number) => (
                  <Option
                    key={index}
                    onClick={() => changeSelectValue(ele)}
                    dangerouslySetInnerHTML={{ __html: ele.name }}
                    selected={selectedValue === ele.name}
                  ></Option>
                ))}
              </OptionsGroup>
            )}
          </>
        ) : type === 'Country' ? (
          <>
            <OptionsGroup>
              {trendingCountryCities?.map((ele: any, index: number) => (
                <Option
                  key={index}
                  onClick={() => changeSelectValue(ele)}
                  dangerouslySetInnerHTML={{ __html: ele.name }}
                  selected={selectedValue === ele.name}
                ></Option>
              ))}
            </OptionsGroup>
            <OptionsGroup>
              {nonTrendingCountryCities
                ?.sort((a, b) => (a.name > b.name ? 1 : -1))
                .map((ele: any, index: number) => (
                  <Option
                    key={index}
                    onClick={() => changeSelectValue(ele)}
                    dangerouslySetInnerHTML={{ __html: ele.name }}
                    selected={selectedValue === ele.name}
                  ></Option>
                ))}
            </OptionsGroup>
          </>
        ) : type === 'Identification' ? (
          <>
            <OptionsGroup>
              {identificationOptions?.map((ele: any, index: number) => (
                <Option
                  key={index}
                  onClick={() => changeSelectValue(ele)}
                  dangerouslySetInnerHTML={{ __html: ele }}
                  selected={selectedValue === ele}
                ></Option>
              ))}
            </OptionsGroup>
          </>
        ) : type === 'LearningType' ? (
          <>
            <OptionsGroup>
              {learningTypes?.map((ele: any, index: number) => (
                <Option
                  key={index}
                  onClick={() => changeSelectValue(ele)}
                  dangerouslySetInnerHTML={{ __html: ele.name }}
                  selected={selectedValue === ele}
                ></Option>
              ))}
            </OptionsGroup>
          </>
        ) : (
          type === 'Custom' && (
            <>
              <OptionsGroup>
                {customOptions?.map((ele: any, index: number) => (
                  <Option
                    key={index}
                    onClick={() => changeSelectValue(ele)}
                    dangerouslySetInnerHTML={{ __html: ele.name }}
                    selected={selectedValue === ele}
                  ></Option>
                ))}
              </OptionsGroup>
            </>
          )
        )}
      </OptionsContainer>
    </Container>
  );
};

export default CustomSelect;
